import { useEffect, FunctionComponent, useState } from 'react';
import { Typography, IconButton, Button, Chip, Paper, Divider, Input, Dialog, Table, TableRow, TableCell, Tooltip } from '../../tailwindcomponents';
import { Collection } from '../../components';
import { FlightScheduleLegend } from '../../components/Line';
import { FlightSchedule, LineWorkPackages, LineTsrFaultDto } from '../../types/line';
import lineApi from '../../api/line';
import EmployeeSearch from '../../components/EmployeeSearch/EmployeeSearch';
import { User } from '../../types/user';
import { FlightTakeoff, Delete, Link } from '../../components/icons';
import { redirectToMxi } from '../../config/mxiProvider';
import { DateTime } from 'luxon';
import { sortRowsByKey } from '../../utils/tableSort';
import { TableColumns } from '../../types/table';

interface Props {
    isLineSupervisorAdmin: Boolean,
    setLoading: Function
}

const sortTableColumns = [
     { label: " ", key: "index", sortable: false },
    { label: "Tail", key: "tailNumber" },
    { label: "Curr Airport", key: "currentAirport" },
    { label: "Stand", key: "currentStand" },
    { label: "Arr Fl, nr.", key: "arrivalFlightNumberAndDepartureAirport" },
    { label: "Arrival", key: "arrivalTime" },
    { label: "Departure", key: "departureTime" },
    { label: "Dep Fl. nr.", key: "departureFlightNumberAndDepartureAirport" },
    { label: "Mechanics", key: "mechanics", sortable: false },
    { label: "TSR", key: "tsr", sortable: false },
    { label: "Remarks", key: "remarks" },
] as TableColumns[];


const LineFlightSchedule: FunctionComponent<Props> = ({ isLineSupervisorAdmin, setLoading }) => {
    const [remarksDialogOpen, setRemarksDialogOpen] = useState(false);
    const [remark, setRemark] = useState<string>("");
    const [mechanicsDialogOpen, setMechanicsDialogOpen] = useState(false);
    const [tailNumber, setTailNumber] = useState("");
    const [selectedEmployees, setSelectedEmployees] = useState<User[]>([]);
    const [mechanicsChanged, setMechanicsChanged] = useState(false);
    const [paperWorkDialogOpen, setPaperWorkDialogOpen] = useState(false);
    const [paperWorkPackages, setPaperWorkPackages] = useState(Array<LineWorkPackages>());

    const [dataFetched, setDataFetched] = useState<string>("");
    const [flightScheduleError, setFlightScheduleError] = useState<boolean>(false); // set the initial state as error so we do not show the table until we have data

    const [flights, setFlightSchedule] = useState<Array<FlightSchedule>>([]);
    const [tsrFaults, setTsrFaults] = useState<LineTsrFaultDto[]>([]);

    useEffect(() => {
        refresh();
    }, []);

    const refresh = () => {
        setLoading(true);
        lineApi.getFlightSchedule().then(res => {
            setFlightScheduleError(false);
            setLoading(false);
            setFlightSchedule(sortRowsByKey(res.data, 'currentAirport', 'asc', true));
            setDataFetched(res.data_fetched_when);
            if (res.data.length === 0) {
                setFlightScheduleError(true);
            }
        }).catch(err => {
            setFlightScheduleError(true);
            setLoading(false);
        });
        lineApi.GetTsrFaults().then(res => {
            setTsrFaults(res.data);
        }).catch(() => {
            setTsrFaults([]);
        });
    }

    useEffect(() => {
        //Using keyup instead of keydown to avoid multiple fetch calls
        const handleKeyUp = (event: KeyboardEvent) => R_key_Pressed(event);
        document.addEventListener('keyup', handleKeyUp, true);
        
        return () => {
            document.removeEventListener('keyup', handleKeyUp, true);
        }
    }, [remarksDialogOpen, mechanicsDialogOpen]); // Include state variables as dependencies
    
    const R_key_Pressed = (event: any) => {
        const keyCode = event.keyCode;
        if(remarksDialogOpen === false && mechanicsDialogOpen === false){
            if (keyCode === 82) {
                event.preventDefault();
                refresh();
            }
        }
    }

    const generateTablePaperworkBody = (row: LineWorkPackages, index: number) => {
        return (
            <TableRow>
                <TableCell>{index}</TableCell>
                <TableCell onClick={() => redirectToMxi(row.wo_barcode)}><IconButton><Link /></IconButton></TableCell>
                <TableCell>{row.wo_status}</TableCell>
                <TableCell>{row.wo_name}</TableCell>
                <TableCell>{row.wo_number}</TableCell>
                <TableCell>{row.start_date_text}</TableCell>
            </TableRow>
        )
    }
    const generateTableBody = (row: FlightSchedule, index: number) => {
        const dotted = row.wo_status_for_aircraft.status === "Incomplete paperwork";
        const onRemarksClicked = (index: number) => {
            if (isLineSupervisorAdmin) {
                setRemark(row.remarks);
                setTailNumber(row.tailNumber);
                setRemarksDialogOpen(true);
            }
        }

        const onMechanicsClicked = (index: number) => {
            if (isLineSupervisorAdmin) {
                setTailNumber(row.tailNumber);
                setSelectedEmployees(row.assigned_mechanics);
                setMechanicsChanged(false);
                setMechanicsDialogOpen(true);
            }
        }
        const onTailNumberClicked = (row: FlightSchedule) => {
            if (row.wo_status_for_aircraft?.lineWorkpackages?.length > 0) {
                setPaperWorkDialogOpen(true);
                setTailNumber(row.tailNumber);
                setPaperWorkPackages(row.wo_status_for_aircraft.lineWorkpackages);

            }
        }
        return (
            <TableRow
                // AOG uses red-300 (the Tool Stock overdue red) on purpose - a product decision, not drift.
                className={(row.wo_status_for_aircraft.status === "In Air" || row.wo_status_for_aircraft.status === "Incomplete paperwork") ? 'bg-[lightyellow]' :
                    row.wo_status_for_aircraft.status === "In Work" ? 'bg-[lightblue]' :
                        row.wo_status_for_aircraft.status === "Closed" ? 'bg-[#63AB62]' :
                            row.wo_status_for_aircraft.status === "AOG" ? 'bg-red-300' : ""
                }
            >
                <TableCell className={dotted ? 'border-t-2 border-b-2 border-l-2 border-dashed border-gray-500' : ""}>{index}</TableCell>
                <TableCell className={dotted ? 'border-t-2 border-b-2 border-dashed border-gray-500' : ""} onClick={() => onTailNumberClicked(row)}>{row.tailNumber}</TableCell>
               
                <TableCell className={dotted ? 'border-t-2 border-b-2 border-x-2 border-dashed border-gray-500' : ""}>
                    {row.currentAirport === "IN-AIR" ? <FlightTakeoff /> : row.currentAirport
                    }
                </TableCell>
                <TableCell className={dotted ? 'border-t-2 border-b-2 border-dashed border-gray-500' : ""}>{row.currentStand}</TableCell>
                <TableCell className={dotted ? 'border-t-2 border-b-2 border-dashed border-gray-500' : ""}>{row.arrivalFlightNumberAndDepartureAirport}</TableCell>
                <TableCell className={dotted ? 'border-t-2 border-b-2 border-dashed border-gray-500' : ""}>{row.arrivalTime ? DateTime.fromJSDate(new Date(row.arrivalTime)).toFormat('dd.MM.yyyy HH:mm') : ''}</TableCell>
                <TableCell className={dotted ? 'border-t-2 border-b-2 border-dashed border-gray-500' : ""}>{row.departureTime ? DateTime.fromJSDate(new Date(row.departureTime)).toFormat('dd.MM.yyyy HH:mm') : ''}</TableCell>
                <TableCell className={dotted ? 'border-t-2 border-b-2 border-dashed border-gray-500' : ""}>{row.departureFlightNumberAndDepartureAirport}</TableCell>
                <TableCell className={dotted ? 'border-t-2 border-b-2 border-dashed border-gray-500' : ""} onClick={() => onMechanicsClicked(index)}>
                    <div className="flex flex-wrap items-center gap-1">
                        {row.assigned_mechanics.map((item, idx) => (
                            <Chip key={idx} size="small" color="primary" label={item.abbreviation ? item.abbreviation : "?"} />
                        ))}
                    </div>
                </TableCell>
                 <TableCell className={dotted ? 'border-t-2 border-b-2 border-dashed border-gray-500' : ""}>
                    {tsrFaults.filter(fault => fault.TailNumber === row.tailNumber).map((fault, idx) => (
                        <Tooltip key={idx} title={fault.TsrBarcode} placement="bottom">
                            <IconButton 
                                size="small"
                                onClick={() => redirectToMxi(fault.TsrBarcode)}
                            >
                                <Link fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    ))}
                </TableCell>
                <TableCell className={dotted ? 'border-t-2 border-b-2 border-r-2 border-dashed border-gray-500' : ""} onClick={() => onRemarksClicked(index)}>{row.remarks}</TableCell>
            </TableRow>
        )
    }

    const onRemoveAllEmployees = () => {
        if (selectedEmployees.length === 0) return;
        Promise.all(selectedEmployees.map(mechanic =>
            lineApi.RemoveEmployeeFlightSchedule(tailNumber, mechanic.user_id)
        )).then(() => {
            setSelectedEmployees([]);
            setMechanicsChanged(true);
        });
    }

    const onSelectedEmployeeRow = (employee: User | null, type: string) => {
        if (type === "Assign" && employee !== null) {
            lineApi.AssignEMployeeFlightSchedule(employee, tailNumber).then(res => {
                if(res.status === true){
                    setSelectedEmployees([...selectedEmployees, employee]);
                    setMechanicsChanged(true);
                }
            });
        }
    }

    const onCloseMechanicDialog = () => {
        setMechanicsDialogOpen(false);
        // Only refetch when something actually changed - opening and closing the
        // dialog to look at the roster shouldn't reload the whole schedule.
        if (mechanicsChanged) {
            refresh();
        }
        setMechanicsChanged(false);
    }

    const onRemoveEmployee = (employeeId: number) => {
        const employeeToRemove = selectedEmployees.findIndex(employee => employee.user_id === employeeId);
        const tempEmployees = [...selectedEmployees];
        tempEmployees.splice(employeeToRemove, 1);
        setSelectedEmployees(tempEmployees);
        lineApi.RemoveEmployeeFlightSchedule(tailNumber, employeeId).then(res => { setMechanicsChanged(true); });
    }

    const onUpdateRemarkToFlightSchedule = (status: boolean) => {
        if (status === true) {
            lineApi.UpdateFlightSchedule(remark, tailNumber).then(res => {
                setRemark("");
                refresh();
            });
        }
        setRemarksDialogOpen(false);
    }

    const onRemoveAllofLineShiftSupervisorEmployees = () => {
        lineApi.RemoveAllofLineShiftSupervisorEmployees().then(res => {
            refresh();
        });
    }
    return (
        <div>
            {flightScheduleError &&
                <div className="mt-[50px]">
                    <Typography alignSelf="center" variant='h3' text={"There was an error fetching the flight schedule. Please try again later or contact Core Solutions"} />
                    <Typography variant='h3' text={"This is most likely a CDATA network issue."} />
                </div>
            }
            <FlightScheduleLegend
                dataFetched={dataFetched}
                onRemoveAllEmployees={isLineSupervisorAdmin ? () => onRemoveAllofLineShiftSupervisorEmployees() : undefined}
            />
            <Table
                rows={flights}
                generateBody={(row: any, index: number) => generateTableBody(row, index)}
                pagination={false}
                skipIndexCol={true}
                sortTableColumns={sortTableColumns}
                isFlightSchedule={true}
                secondarySortKey="arrivalTime"
            />
            <Dialog
                visible={mechanicsDialogOpen}
                max_width={"md"}
                title={""}
                hideCloseButton
                context={
                    <div>
                        <div className="w-full items-center">
                            <Paper elevation={3} className="p-[6px] m-1">
                                <EmployeeSearch autoFocusOnInput searchByAbbreviation place_holder={"Search by abbreviation"} clearOnSelect fullUser={true} onSelectedEmployee={(employee: User | null) => onSelectedEmployeeRow(employee, "Assign")} />
                            </Paper>
                            <Divider className="mt-[26px] mb-6" />
                            <Paper elevation={3} className="p-[6px] m-1">
                                <EmployeeSearch place_holder={"Search by name"} clearOnSelect fullUser={true} onSelectedEmployee={(employee: User | null) => onSelectedEmployeeRow(employee, "Assign")} />
                            </Paper>
                            <Collection background={false} collectionList={selectedEmployees} title="Employees selected" idProperty="user_id" removeItem={(itemId: number) => onRemoveEmployee(itemId)} />
                            {selectedEmployees.length > 0 &&
                                <div className="flex flex-wrap justify-end">
                                    <Button variant="text" color="error" size="small" startIcon={<Delete />} onClick={() => onRemoveAllEmployees()}>Remove all employees</Button>
                                </div>
                            }
                        </div>
                    </div>
                }
                onClose={(status: any) => onCloseMechanicDialog()}
                strict={false}
                show_options={false}
            />
            <Dialog
                visible={remarksDialogOpen}
                max_width={"sm"}
                title={"Remarks"}
                context={
                    <div>
                        <Input
                            multiline={true}
                            help_text={""}
                            label_text={"Remarks"}
                            value={remark}
                            onTextChange={(text: string) => setRemark(text)}
                            type="text"
                            width={100}
                        />
                    </div>
                }
                onClose={(status: any) => onUpdateRemarkToFlightSchedule(status)}
                strict={false}
                show_options={true}
            />
            <Dialog
                visible={paperWorkDialogOpen}
                max_width={"md"}
                title={`Paperwork for : ${tailNumber}`}
                context={
                    <div>
                        <Table
                            headers={["Mxi", "Status", "Wo name", "Wo number", "start"]}
                            rows={paperWorkPackages}
                            generateBody={(row: any, index: number) => generateTablePaperworkBody(row, index)}
                            pagination={false}
                            skipIndexCol={false}
                        />
                    </div>
                }
                onClose={(status: any) => { setPaperWorkDialogOpen(false); setPaperWorkPackages([]) }}
                strict={false}
                show_options={false}
            />
        </div>
    )
}
export default LineFlightSchedule;


